import { apiClient } from '../../api_actions/index';
import { Space, Table, Button, Modal, Form, Input, Select, DatePicker, Upload, InputNumber, Switch, Popconfirm, App, ColorPicker, Spin, Divider, Typography, UploadProps } from 'antd';
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import dayjs from 'dayjs';
import { UploadOutlined } from '@ant-design/icons';
import { isBeforeToday, parseDateForUI } from '../../utils';
import { useIsSuperAdmin ,useIsAdmin, useMobileStore, usePaginationStore} from '../../store';
import LoadingOrbitalSpinner from '../../components/LoadingOrbitalSpinner/LoadingOrbitalSpinner';
import { REGEX_PATTERNS } from '../../constants/enums';
import './style.scss'

const { Paragraph } = Typography;

const ModelEditTable = forwardRef(function ModelEditTable({
  createEndpoint,
  readEndpoint,
  editEndpoint,
  deleteEndpoint,
  copyEndpoint,
  searchQuery = "",
  secondarySearchQuery = "",
  searchInput = "",
  secondarySearchInput = "",
  selectedCustomer = null,
  selectedDetectionType = null,
  selectedLocation = null,
  selectedValidation = null,
  columns,
  formFields,
  continuousRefresh = false,
  refreshToken = 0,
  title = '',
  rowKey = 'id',
  userRole = 0,
  canCreate = true,
  canView = true,//Should be renamed canView or something
  canSave = true,
  canDelete = true,
  canCopy = false,
  expandedRowRender = null,
  transformBeforeSave = null,
  rowClassName = () => '',
  addedDataColumn = '',
  addedDataColumnLabel = '',
  addedDataColumnName = '',
  showAddButton = true,
  infinatePages = false,
  reloadTimestamp = null,
}, ref) {
  const { message } = App.useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const isCopyingRef = useRef(false);
  const [form] = Form.useForm();
  const [filePreview, setFilePreview] = useState({});
  const isMobile = useMobileStore((state) => state.isMobile);
  const isAdmin = useIsAdmin();
  const isSuperAdmin = useIsSuperAdmin();
  const [debouncedSearchInput, setDebouncedSearchInput] = useState(searchInput);
  const [debouncedSecondarySearchInput, setDebouncedSecondarySearchInput] = useState(secondarySearchInput)
  const debounceTimer = useRef(null);
  const pagination = usePaginationStore((state) => state.pagination);
  const updatePagination = usePaginationStore((state) => state.updatePagination);
  const [deleteConfirmation, setDeleteConfirmation] = useState({});
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const MINUTE_MS = 6000
  // Add state for dynamic field options
  const [fieldOptions, setFieldOptions] = useState({});
  const [fieldSourceData, setFieldSourceData] = useState({});

  if (continuousRefresh) {
    useEffect(() => {
      const interval = setInterval(() => {
        loadData(pagination.current, pagination.pageSize);
        console.log('Logs every minute');
      }, MINUTE_MS);
      return () => clearInterval(interval); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
    }, [])
  }

  const formatValue = (value) => { //DELETE?
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    // If it's a string, try to parse and reformat it
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      // If parsing fails, return as-is
      return value;
    }
  };
  // Debounce search input - 1 second delay, only if more than 3 characters
  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      if (searchInput.length > 3) {
        setDebouncedSearchInput(searchInput);
      } else if (searchInput.length === 0) {
        setDebouncedSearchInput('');
      }
    }, 1000);

    return () => clearTimeout(debounceTimer.current);
  }, [searchInput]);

  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      if (secondarySearchInput.length > 3) {
        setDebouncedSecondarySearchInput(secondarySearchInput);
      } else if (secondarySearchInput.length === 0) {
        setDebouncedSecondarySearchInput('');
      }
    }, 1000);

    return () => clearTimeout(debounceTimer.current);
  }, [secondarySearchInput]);

  const beforeUpload = (file, fieldName) => { //DELETE?
    const isPNG = file.type === 'image/png';
    if (!isPNG) {
      message.error('Only PNG files are allowed!');
      return Upload.LIST_IGNORE;
    }
    const reader = new FileReader();
    reader.onload = e => {
      setFilePreview(prev => ({ ...prev, [fieldName]: e.target.result }));
      // Note: You need to handle onChange for the form field
      form.setFieldValue(fieldName, e.target.result);
    };
    reader.readAsDataURL(file);
    return false;
  };

  useEffect(() => {
    //if (reloadTimestamp === null) return;
    loadData(pagination.current, pagination.pageSize);
    loadFieldOptions(); // Load options on mount
  }, [readEndpoint, refreshToken, debouncedSearchInput, debouncedSecondarySearchInput,selectedCustomer, selectedDetectionType, selectedLocation, selectedValidation, reloadTimestamp]);

  // Function to load dynamic options
  const loadFieldOptions = async () => {
    const fieldsNeedingOptions = formFields.filter(field => field.optionsEndpoint);
    if (fieldsNeedingOptions.length === 0) return;

    try {
      const optionsPromises = fieldsNeedingOptions.map(async (field) => {
        const [sourceResponse, crossRefResponse] = await Promise.all([
          apiClient.post(field.optionsEndpoint),
          field.crossReferenceEndpoint ? apiClient.post(field.crossReferenceEndpoint) : Promise.resolve(null),
        ]);

        const sourceItems = sourceResponse?.data || [];
        const crossReferenceItems = crossRefResponse?.data || [];

        const options = sourceItems.map(item => {
          let label;

          // Check if optionsLabel is an array (multiple fields)
          if (Array.isArray(field.optionsLabel)) {
            label = field.optionsLabel
              .map(fieldName => item[fieldName])
              .filter(val => val)
              .join(field.labelSeparator || ' - ');
          } else {
            label = item[field.optionsLabel || 'name'];
          }

          return {
            label: label,
            value: item[field.optionsValue || 'id'],
          };
        });

        return { fieldName: field.name, options, sourceItems, crossReferenceItems };
      });

      const optionsResults = await Promise.all(optionsPromises);

      const optionsMap = {};
      const sourceDataMap = {};
      optionsResults.forEach(({ fieldName, options, sourceItems, crossReferenceItems }) => {
        optionsMap[fieldName] = options;
        sourceDataMap[fieldName] = { sourceItems, crossReferenceItems };
      });

      setFieldOptions(optionsMap);
      setFieldSourceData(sourceDataMap);
    } catch (error) {
      console.error('Failed to load field options:', error);
    }
  };

  // Compute select options, applying filterFn when defined
  const getSelectOptions = (field) => {
    if (!field.optionsEndpoint) return field.options;
    if (field.filterFn && fieldSourceData[field.name]) {
      const { sourceItems, crossReferenceItems } = fieldSourceData[field.name];
      return sourceItems
        .filter(item => field.filterFn(item, editingRecord))
        .map(item => {
          let label;
          if (Array.isArray(field.optionsLabel)) {
            label = field.optionsLabel
              .map(f => item[f])
              .filter(v => v)
              .join(field.labelSeparator || ' - ');
          } else {
            label = item[field.optionsLabel || 'name'];
          }
          return { label, value: item[field.optionsValue || 'id'] };
        });
    }
    return fieldOptions[field.name];
  };

  const createAddedDataColumn = () => {
    return {
      title: addedDataColumnLabel || '',
      key: addedDataColumnLabel || '',
      render: (_, record) => {
        const data = record[addedDataColumn];
        if (Array.isArray(data)) {
          return data.map((item, idx) => (
            <span className='data-square' key={idx}>{item[addedDataColumnName]}</span>
          ));
        }

        return data || '-';
      }
    };
  };

  const loadData = async (page, pageSize) => {
    setLoading(true);
    const offset = (page - 1) * pageSize;
    const limit = page * pageSize

    try {
      const readEndpointUrl = new URL(readEndpoint, "http://dummy-base");
      readEndpointUrl.searchParams.set("limit", limit);
      readEndpointUrl.searchParams.set("offset", offset);
      // Add search query parameter if search is enabled and input is not empty
      if (searchQuery && debouncedSearchInput && debouncedSearchInput.length > 3) {
        readEndpointUrl.searchParams.set(searchQuery, debouncedSearchInput);
      }

      if (secondarySearchQuery && debouncedSecondarySearchInput && debouncedSecondarySearchInput.length > 3) {
        readEndpointUrl.searchParams.set(secondarySearchQuery, debouncedSecondarySearchInput);
      }
      // Add customer_name parameter if a customer is selected
      if (selectedCustomer) {
        readEndpointUrl.searchParams.set("customer_id", selectedCustomer);
      }
      // Add detection_type parameter if a detection type is selected
      if (selectedDetectionType) {
        readEndpointUrl.searchParams.set("detection_type_name", selectedDetectionType);
      }
      // Add location parameter if a location is selected
      if (selectedLocation) {
        readEndpointUrl.searchParams.set("location_id", selectedLocation);
      }
      if (selectedValidation) {
        readEndpointUrl.searchParams.set("validation", selectedValidation);
      }
      const readEndpointString = readEndpointUrl.pathname + readEndpointUrl.search.replaceAll('+', '%20');

      const result = await apiClient.post(readEndpointString);
      setData(result?.data || []);
      if (!infinatePages && result?.data && result.data.length && result.data.length < limit) {
        updatePagination(prev => ({
            ...prev,
            total: offset + result.data.length
        }));
      } else {
        updatePagination(prev => ({
            ...prev,
            total: (page + 1) * pageSize
        }));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    }
    setLoading(false);
  };

  //This renders the actual popup modal that you get when you click a row
  const handleRowClick = (record) => {

    if (!canView) return;
    setEditingRecord(record);
    const formValues = { ...record };
    formFields.forEach(field => {

      if (field.type === 'select' && field.optionsValue) {
        if (!Array.isArray(field.name)) {
          const fieldBaseName = field.name.replace(/_id$/, '');
          if (record[fieldBaseName] && typeof record[fieldBaseName] === 'object' && !Array.isArray(record[fieldBaseName])) {
            formValues[field.name] = record[fieldBaseName][field.optionsValue];
          }
        }
      }

      if (field.type === 'date' && formValues[field.name]) {
        // Check if date is -1 (unset) or invalid - if so, set to null
        const dateValue = formValues[field.name];
        const timestamp = new Date(dateValue).getTime()
        if (dateValue === -1 || dateValue === '-1' || timestamp === -1000) {
          formValues[field.name] = null;
        } else {
          formValues[field.name] = dayjs(formValues[field.name]);
        }
      }

      if (field.type === 'tel' && formValues[field.name]) {
        let phoneValue = formValues[field.name].replace(/\D/g, '');
        if (phoneValue.startsWith('1')) {
          phoneValue = phoneValue.substring(1);
        }
        if (phoneValue.length === 10) {
          formValues[field.name] = `${phoneValue.substring(0, 3)}-${phoneValue.substring(3, 6)}-${phoneValue.substring(6)}`;
        }
      }
    });

    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();

    // Set default values for switch fields (and any others with defaults)
    const defaults = {};
    formFields.forEach(field => {
      if (field.type === 'switch') {
        defaults[field.name] = field.defaultValue ?? false;
      }
    });
    form.setFieldsValue(defaults);

    setModalVisible(true);
  };


  useImperativeHandle(ref, () => ({
    openAdd: handleAdd,
    expandAll: () => setExpandedRowKeys(data.map(row => row[rowKey])),
    collapseAll: () => setExpandedRowKeys([]),
  }));

  const handleSave = async (values) => {
    try {
      let apiValues = { ...values };
      // Handle date conversions and phone number formatting
      formFields.forEach(field => {
        if (field.type === 'date') {
          if (apiValues[field.name]) {
            apiValues[field.name] = apiValues[field.name].toISOString();
          } else {
            apiValues[field.name] = new Date(-1).toISOString()
          }
        }
        if (field.type === 'tel' && apiValues[field.name]) {
          // Remove hyphens and whitespace, add +1 prefix
          let phoneValue = apiValues[field.name].replace(REGEX_PATTERNS.PHONE_STRIP_CHARS, '');
          if (!phoneValue.startsWith('+1')) {
            phoneValue = '+1' + phoneValue;
          }
          apiValues[field.name] = phoneValue;
        }
        // if (field.type === 'switch' && apiValues[field.name]) {
        //   apiValues[field.name] = apiValues[field.name]//.toISOString();
        // }

        if (field.type === 'color' && apiValues[field.name]) {
            // Convert color to RGB with alpha 0.5
            let colorValue = apiValues[field.name];
            
            if (typeof colorValue === 'object') {
              // Get RGB values from the color object
              const rgb = colorValue.toRgb();
              // Force alpha to 0.5
              apiValues[field.name] = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.5)`;
            } else if (typeof colorValue === 'string') {
              // If it's already a string, parse and rebuild with alpha 0.5
              const match = colorValue.match(REGEX_PATTERNS.COLOR_RGB_PARSE);
              if (match) {
                apiValues[field.name] = `rgba(${match[1]}, ${match[2]}, ${match[3]}, 0.5)`;
              }
            }
          }

        // if (field.type === 'upload' && apiValues[field.name]) {
        //   apiValues[field.name] = apiValues[field.name]
        // }

      });

      // Apply custom transformation if provided
      if (transformBeforeSave) {
        apiValues = transformBeforeSave(apiValues, editingRecord);
      }
      if (editingRecord) {
        if (isCopyingRef.current) {
          await apiClient.post(copyEndpoint, apiValues);
          isCopyingRef.current = false
        } else {
          await apiClient.post(editEndpoint, apiValues);
          isCopyingRef.current = false
        }
      } else {
        await apiClient.post(createEndpoint, apiValues);
        isCopyingRef.current = false
      }
      setModalVisible(false);
      loadData(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Failed to save:', error);
    }
  };

  const handleDelete = async (values) => {
    try {

      let apiValues = { ...values };

      await apiClient.post(deleteEndpoint, apiValues);
      loadData(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const finalColumns = addedDataColumn ? [...columns, createAddedDataColumn()] : columns;

  const columnsWithActions = [
    ...finalColumns,
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const confirmKey = `delete-${record.key || record.id}`;
        const isConfirmValid = deleteConfirmation[confirmKey] === 'Delete';

        return (
          <Popconfirm
            title="Confirm deletion"
            description={
              <div onClick={(e) => e.stopPropagation()}>
                <p>Type "Delete" to confirm:</p>
                <Input
                  placeholder="Type Delete"
                  value={deleteConfirmation[confirmKey] || ''}
                  onChange={(e) => {
                    e.stopPropagation();
                    setDeleteConfirmation({
                      ...deleteConfirmation,
                      [confirmKey]: e.target.value
                    });
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  onPressEnter={(e) => {
                    e.stopPropagation();
                    if (isConfirmValid) {
                      handleDelete(record);
                      setDeleteConfirmation({
                        ...deleteConfirmation,
                        [confirmKey]: ''
                      });
                    }
                  }}
                />
              </div>
            }
            okText="Delete"
            okButtonProps={{ danger: true, disabled: !isConfirmValid }}
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDelete(record);
              setDeleteConfirmation({
                ...deleteConfirmation,
                [confirmKey]: ''
              });
            }}
            onCancel={(e) => {
              e?.stopPropagation();
              setDeleteConfirmation({
                ...deleteConfirmation,
                [confirmKey]: ''
              });
            }}
          >
            <Button
              danger
              size="small"
              onClick={(e) => e.stopPropagation()}
            >
              Delete
            </Button>
          </Popconfirm>
        );
      },
    }
  ];

  // Returns standard validation rules per field type, merged alongside consumer-provided field.rules
  const getTypeValidationRules = (field) => {
    switch (field.type) {
      case 'email':
        return [
          {
            pattern: REGEX_PATTERNS.EMAIL,
            message: 'Please enter a valid email address',
          },
        ];
      case 'name':
        return [
          {
            pattern: REGEX_PATTERNS.NAME_CHARS,
            message: "Name may only contain letters, spaces, hyphens, and apostrophes",
          },
        ];
      case 'textarea':
      case 'password':
        return [];
      case 'default':
      default:
        if (field.type === 'select' || field.type === 'date' || field.type === 'switch'
          || field.type === 'number' || field.type === 'button' || field.type === 'upload'
          || field.type === 'color' || field.type === 'endpoint' || field.type === 'json') {
          return [];
        }
        return [
          {
            pattern: REGEX_PATTERNS.GENERAL_INPUT,
            message: 'No special characters allowed',
          },
        ];
    }
  };

  const groupFields = (fields) => {
    const result = [];
    const seen = new Set();

    fields.forEach(field => {
      if (!field.group) {
        // Ungrouped — add as-is
        result.push(field);
      } else if (!seen.has(field.group)) {
        // First time seeing this group — add all fields in the group together
        seen.add(field.group);
        result.push(fields.filter(f => f.group === field.group));
      }
      // Subsequent fields in same group are skipped (already added above)
    });

    return result; // mix of single field objects and arrays of field objects
  };


  const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      // reader.result is something like:
      // "data:image/png;base64,iVBORw0KGgoAAAANS..."
      resolve(reader.result);
    };

    reader.onerror = reject;

    reader.readAsDataURL(file);
  });

  const customFileUpload = async ({ file, onSuccess, onError, onProgress }) => {
      var b64 = await fileToBase64(file)
      const b64_data = b64.split(",")[1];
      form.setFieldValue("data", b64_data);
  };

  const renderFormField = (field, isEditing) => {
    const roleDisabled = field.minRoleToEdit && userRole < field.minRoleToEdit;
    const isDisabled = roleDisabled || (isEditing ? field.disableOnEdit : field.disableOnCreate) || field.disabled;

    switch (field.type) {
      case 'upload':
        return (
          <Upload customRequest={customFileUpload}>
            <Button>Upload</Button>
          </Upload>
        );

      case 'select':
        return (
          <Select
            placeholder={field.placeholder}
            showSearch={field.searchable}
            mode={field.mode}
            onChange={field.onChange}
            allowClear={field.allowClear}
            filterOption={(input, option) =>
              option.label?.toLowerCase().includes(input.toLowerCase())
            }
            options={getSelectOptions(field)}
            loading={field.optionsEndpoint && !fieldOptions[field.name]}
            disabled={isDisabled || field.disabled}
          />
        );

      case 'imageSelect':
        return (
          <Select
            placeholder={field.placeholder}
            showSearch={field.searchable}
            mode={field.mode}
            allowClear={field.allowClear}
            filterOption={(input, option) =>
              option.label?.toLowerCase().includes(input.toLowerCase())
            }
            options={getSelectOptions(field)}
            loading={field.optionsEndpoint && !fieldOptions[field.name]}
            disabled={isDisabled || field.disabled}
            optionRender={(option) => (
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <img
                  src={`${process.env.REACT_APP_DOWNLOAD_URL}/assets/logo?logo_id=${option.value}&token=${localStorage.getItem('token')}`}
                  alt={option.label}
                  style={{ width: 20, height: 20, objectFit: "cover", borderRadius: 4 }}
                />
                {option.label}
              </span>
            )}
            labelRender={(option) => (
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <img
                  src={`${process.env.REACT_APP_DOWNLOAD_URL}/assets/logo?logo_id=${option.value}&token=${localStorage.getItem('token')}`}
                  alt={option.label}
                  style={{ width: 20, height: 20, objectFit: "cover", borderRadius: 4 }}
                />
                {option.label}
              </span>
            )}
          />
        );

      case 'date':
        return (
          <DatePicker
            value={parseDateForUI(fieldOptions[field.name])}
            placeholder={field.placeholder}
            format="YYYY-MM-DD"
            disabled={isDisabled}
            style={{ width: '100%' }}
          />
        );

      case 'textarea':
        return (
          <>
            <Input.TextArea
              rows={1}
              placeholder={field.placeholder}
              disabled={field.disabled || isDisabled}
              onChange={(e) => {
                const sanitized = e.target.value.replace(REGEX_PATTERNS.NON_GENERAL_INPUT, '');
                form.setFieldValue(field.name, sanitized);
              }}
              onPressEnter={(e) => {
                e.preventDefault(); // Prevents default behavior (new line)
              }}
            />
          </>
        );

      case 'list':
        return (
          <Select
            mode="tags"
            placeholder={field.placeholder}
            disabled={field.disabled || isDisabled}
            style={{ width: '100%' }}
          />
        );

      case 'email':
        return (
          <Input
            type="email"
            placeholder={field.placeholder || 'you@example.com'}
            disabled={isDisabled}
            autoComplete="off"
            onChange={(e) => {
              const sanitized = e.target.value.replace(REGEX_PATTERNS.NON_EMAIL_CHARS, '');
              form.setFieldValue(field.name, sanitized);
            }}
            onPressEnter={(e) => {
              e.preventDefault(); // Prevents default behavior (new line)
            }}
          />
        );

      case 'password':
        return (
          <Input.Password
            placeholder={field.placeholder || 'password'}
            disabled={isDisabled}
            autoComplete="off"
            value={form.getFieldValue(field.name) || ''}  // ← controlled value
            onChange={(e) => {
              form.setFieldValue(field.name, e.target.value);
            }}
            onPressEnter={(e) => {
              e.preventDefault();
            }}
          />
        );

      case 'name':
        return (
          <Input
            placeholder={field.placeholder}
            disabled={isDisabled}
            onChange={(e) => {
              const sanitized = e.target.value.replace(REGEX_PATTERNS.NON_NAME_CHARS, '');
              form.setFieldValue(field.name, sanitized);
            }}
            onPressEnter={(e) => {
              e.preventDefault(); // Prevents default behavior (new line)
            }}
          />
        );

      case 'switch':
        return (
          <Switch
            disabled={isDisabled}
          />
        );

      case 'number':
        return <InputNumber
          placeholder={field.placeholder}
          disabled={isDisabled}
          style={{ width: '100%' }}
          min={field.min}
          max={field.max}
          step={field.step || 1}
          precision={field.precision}  // Number of decimal places
        />

      case 'button':
        return (
          <Button
            type={field.buttonType || 'primary'}
            danger={field.danger || false}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              field.onClick && field.onClick(editingRecord);
              setModalVisible(false)
            }}
            disabled={isDisabled || field.disabled}
            block
          >
            {field.label}
          </Button>
        );

      case 'tel':
        return (
          <Input
            type="tel"
            placeholder={field.placeholder}
            disabled={isDisabled}
            autoComplete="off"
            pattern={field.pattern}
            onPressEnter={(e) => {
              e.preventDefault(); // Prevents default behavior (new line)
            }}
          />
        );

      case 'divider':
        return(
          <Divider style={{ borderColor: '#001529' }} orientation="left">
            {field.name}
          </Divider>
        )

      case 'endpoint':
        return (
          <Input
            placeholder={field.placeholder}
            disabled={isDisabled}
            onChange={(e) => {
              const sanitized = e.target.value.replace(REGEX_PATTERNS.NON_ENDPOINT_CHARS, '');
              form.setFieldValue(field.name, sanitized);
            }}
            onPressEnter={(e) => {
              e.preventDefault(); // Prevents default behavior (new line)
            }}
          />
        );

      case 'color':
        return (
          <ColorPicker
            disabled={isDisabled}
            showText
            format="rgb"
            disabledAlpha={false} // Enable alpha since you're using rgba
            presets={field.presets}
            panelRender={field.presets ? (_, { components: { Presets } }) => (
              <div style={{ padding: 8 }}>
                <Presets />
              </div>
            ) : {}}
          />

        );

      default:
        return (
          <Input
            placeholder={field.placeholder}
            disabled={isDisabled}
            onChange={(e) => {
              const sanitized = e.target.value.replace(REGEX_PATTERNS.NON_GENERAL_INPUT, '');
              form.setFieldValue(field.name, sanitized);
            }}
          />
        );
    }
  };

  return (
  <>
    {title && (
      <div>
        <h2>{title}</h2>
      </div>
    )}
      {showAddButton && canCreate && isAdmin && (
        <Button type="primary" onClick={handleAdd} style={{ marginBottom: 16, float: 'right', zIndex: 1 }}>
          Add New
        </Button>
      )}

      <Spin spinning={loading} indicator={<LoadingOrbitalSpinner />}>
        <Table
          columns={canDelete && isAdmin ? columnsWithActions : finalColumns}
          dataSource={data}
          rowKey={rowKey}
          expandable={expandedRowRender ? {
            expandedRowRender,
            expandedRowKeys,
            onExpand: (expanded, record) => {
              const key = record[rowKey];
              setExpandedRowKeys(prev =>
                expanded ? [...prev, key] : prev.filter(k => k !== key)
              );
            },
            expandRowByClick: false,
          } : undefined}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: canView ? 'pointer' : 'default' }
          })}
          rowClassName={rowClassName}
          pagination={infinatePages ? {
            ...pagination,
            showSizeChanger: false, // optional, often disabled when total unknown
          } : {
            ...pagination,
            showSizeChanger: false, // optional, often disabled when total unknown
            total: data.length
          }}
          onChange={(newPagination) => {
            updatePagination(newPagination)
            loadData(newPagination.current, newPagination.pageSize);
          }}
        />
      </Spin>

      <Modal
        title={editingRecord ? `Edit ${title}` : `Add ${title}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            {canCopy && 
              <Button
                onClick={() => {
                    isCopyingRef.current = true
                    form.submit()
                    setModalVisible(false)
                  }
                }
              >
                Copy
              </Button>
            }
            <CancelBtn />
            <OkBtn />
          </>
        )}
        width={600}
        okButtonProps={{ disabled: !canSave}}
      >
      <Form form={form} onFinish={handleSave} layout="vertical">
        {groupFields(formFields).map((fieldOrGroup) => {

          // Grouped — render inline
          if (Array.isArray(fieldOrGroup)) {
            return (
              <Space key={fieldOrGroup[0].group} align="start">
                {fieldOrGroup.map(field => (
                  <Form.Item
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    rules={[...(field.rules || []), ...getTypeValidationRules(field)]}
                    valuePropName={field.type === 'switch' ? 'checked' : 'value'}
                  >
                    {renderFormField(field, !!editingRecord)}
                  </Form.Item>
                ))}
              </Space>
            );
          }

          // Ungrouped — render normally
          const field = fieldOrGroup;
          return (
            <Form.Item
              key={field.name}
              name={field.name}
              label={field.label}
              rules={[...(field.rules || []), ...getTypeValidationRules(field)]}
              valuePropName={field.type === 'switch' ? 'checked' : 'value'}
            >
              {renderFormField(field, !!editingRecord)}
            </Form.Item>
          );

        })}
      </Form>
      </Modal>
  </>
  );
});

export default ModelEditTable;